import { useCallback, useEffect, useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import { Star, StarHalf, X } from 'lucide-react'
import { adminApi, unwrap } from '../../services/api'
import LoadErrorState from '../common/LoadErrorState'
import {
  SERVER_ERROR_MESSAGE,
  SERVER_TIMEOUT_MESSAGE,
  withServerTimeout,
} from '../common/loadErrorUtils'
import { useColors } from '../../hooks/useColors'

interface PartnerRow {
  id: string
  name?: string
  phone?: string
  email?: string | null
  address?: string | null
  zone?: string | null
  is_active?: boolean
  order_count?: number | string
  order_value_total?: number | string
  quality_score?: number | string | null
}

interface PartnerPerformance {
  total_orders: number
  this_month: number
  quality_score: number
  avg_turnaround_hours: number | null
  damage_rate_percent: number
  complaints: number
  revenue_generated: number
  on_time_percent: number
}

interface PartnerOrder {
  id: string
  order_number?: string
  status?: string
  total?: number
  payment_status?: string
  created_at?: string
  updated_at?: string
  customer_name?: string
  customer_phone?: string
  item_count?: number | string
}

interface PartnerOrdersPayload {
  orders?: PartnerOrder[]
  total?: number
  page?: number
  limit?: number
}

interface PartnerPayload {
  data?: PartnerRow[]
}

const formatCurrency = (value?: number | string | null) => {
  const amount = Number(value || 0)
  return `₹${amount.toLocaleString('en-IN')}`
}

const getInitials = (name?: string | null) => {
  if (!name) {
    return 'PT'
  }

  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

const getStatusLabel = (partner: PartnerRow) => (partner.is_active ? 'Active' : 'Inactive')

const clampScore = (value: number) => Math.max(0, Math.min(5, value))

const formatScore = (value?: number | string | null) => {
  if (value === undefined || value === null) {
    return 0
  }
  const parsed = Number(value)
  return Number.isNaN(parsed) ? 0 : clampScore(parsed)
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return '-'
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return String(value)
  }
  return parsed.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const buildMonthlyTrend = (orders: PartnerOrder[]) => {
  const counts = new Map<string, number>()
  orders.forEach((order) => {
    if (!order.created_at) {
      return
    }
    const date = new Date(order.created_at)
    if (Number.isNaN(date.getTime())) {
      return
    }
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    counts.set(key, (counts.get(key) || 0) + 1)
  })

  const now = new Date()
  const months = [] as Array<{ key: string; label: string; count: number }>
  for (let offset = 5; offset >= 0; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - offset, 1)
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    const label = date.toLocaleString('en-IN', { month: 'short' })
    months.push({ key, label, count: counts.get(key) || 0 })
  }

  return months
}

export default function PartnersTab() {
  const colors = useColors()
  const [partners, setPartners] = useState<PartnerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [selectedPartner, setSelectedPartner] = useState<PartnerRow | null>(null)
  const [partnerPerformance, setPartnerPerformance] = useState<PartnerPerformance | null>(null)
  const [partnerOrders, setPartnerOrders] = useState<PartnerOrder[]>([])
  const [partnerOrdersTotal, setPartnerOrdersTotal] = useState(0)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')
  const [scoreInput, setScoreInput] = useState('')
  const [savingScore, setSavingScore] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
  })

  const fetchPartners = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await withServerTimeout(adminApi.getPartners())
      const payload = unwrap<PartnerPayload | PartnerRow[]>(response)

      if (Array.isArray(payload)) {
        setPartners(payload)
        return
      }

      setPartners(Array.isArray(payload?.data) ? payload.data : [])
    } catch (loadError) {
      setError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetchPartners()
  }, [fetchPartners])

  const stats = useMemo(() => {
    const active = partners.filter((partner) => partner.is_active === true).length
    const inactive = partners.filter((partner) => partner.is_active === false).length
    const totalRevenue = partners.reduce((sum, partner) => sum + Number(partner.order_value_total || 0), 0)

    return { active, inactive, totalRevenue }
  }, [partners])

  const handleCreatePartner = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    await adminApi.createPartner({
      name: formData.name,
      phone: formData.phone,
      email: formData.email || undefined,
      address: formData.address,
    })

    setShowCreateModal(false)
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
    })
    await fetchPartners()
  }

  const handleSuspendPartner = async (id: string) => {
    await adminApi.suspendPartner(id)
    await fetchPartners()
  }

  const loadPartnerDetails = useCallback(
    async (partner: PartnerRow) => {
      setSelectedPartner(partner)
      setDetailError('')
      setDetailLoading(true)
      setScoreInput(String(formatScore(partner.quality_score)))

      try {
        const [performanceResponse, ordersResponse] = await Promise.all([
          withServerTimeout(adminApi.getPartnerPerformance(partner.id)),
          withServerTimeout(adminApi.getPartnerOrders(partner.id, { page: 1, limit: 50 })),
        ])

        setPartnerPerformance(performanceResponse as PartnerPerformance)

        const ordersPayload = ordersResponse as PartnerOrdersPayload
        setPartnerOrders(Array.isArray(ordersPayload?.orders) ? ordersPayload.orders : [])
        setPartnerOrdersTotal(Number(ordersPayload?.total || 0))
      } catch (loadError) {
        setDetailError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
      } finally {
        setDetailLoading(false)
      }
    },
    [],
  )

  const handleScoreSave = async () => {
    if (!selectedPartner) {
      return
    }

    const scoreValue = Number(scoreInput)
    if (Number.isNaN(scoreValue) || scoreValue < 0 || scoreValue > 5) {
      setDetailError('Quality score must be between 0 and 5.')
      return
    }

    setSavingScore(true)
    setDetailError('')

    try {
      await adminApi.updatePartnerScore(selectedPartner.id, { quality_score: scoreValue })
      await fetchPartners()
      await loadPartnerDetails({ ...selectedPartner, quality_score: scoreValue })
    } catch (saveError) {
      setDetailError(saveError instanceof Error && saveError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      setSavingScore(false)
    }
  }

  const trendData = useMemo(() => buildMonthlyTrend(partnerOrders), [partnerOrders])
  const maxTrendCount = trendData.reduce((max, entry) => Math.max(max, entry.count), 0)

  return (
    <div>
      <div style={{ margin: '20px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
          {partners.length} Partners
        </h2>

        <button
          type="button"
          className="font-body"
          onClick={() => setShowCreateModal(true)}
          style={{
            border: 'none',
            background: '#D6B97B',
            color: colors.textPrimary,
            fontSize: '14px',
            fontWeight: 600,
            padding: '10px 20px',
            borderRadius: '10px',
            cursor: 'pointer',
          }}
        >
          + Add Partner
        </button>
      </div>

      <div style={{ margin: '0 32px 20px', display: 'flex', gap: '12px' }}>
        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Active
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: '#15803D' }}>
            {stats.active}
          </p>
        </div>

        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '12px', padding: '16px', border: `1.5px solid ${colors.border}` }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Inactive
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: colors.textSecondary }}>
            {stats.inactive}
          </p>
        </div>

        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '12px', padding: '16px', border: '1.5px solid #D6B97B' }}>
          <p className="font-body" style={{ margin: '0 0 6px', fontSize: '11px', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Revenue
          </p>
          <p className="font-display" style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: '#D6B97B' }}>
            {formatCurrency(stats.totalRevenue)}
          </p>
        </div>
      </div>

      <div
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          margin: '0 32px 32px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.bgSecondary, borderBottom: `1px solid ${colors.border}` }}>
              {['Photo', 'Name', 'Phone', 'Zone', 'Orders', 'Quality', 'Revenue', 'Status', 'Actions'].map((header) => (
                <th
                  key={header}
                  className="font-body"
                  style={{
                    padding: '16px 20px',
                    fontSize: '11px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'left',
                  }}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {error && !loading ? (
              <tr>
                <td colSpan={9}>
                  <LoadErrorState message={error} onRetry={() => void fetchPartners()} margin="24px" />
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={9} style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    Loading partners...
                  </p>
                </td>
              </tr>
            ) : partners.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '32px 20px', textAlign: 'center' }}>
                  <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                    No partners found.
                  </p>
                </td>
              </tr>
            ) : (
              partners.map((partner) => {
                const isActive = partner.is_active !== false

                return (
                  <tr key={partner.id} style={{ borderBottom: `0.5px solid ${colors.surface}` }}>
                    <td style={{ padding: '16px 20px' }}>
                      <div
                        className="font-body"
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '999px',
                          background: '#D6B97B',
                          color: colors.textPrimary,
                          display: 'grid',
                          placeItems: 'center',
                          fontSize: '13px',
                          fontWeight: 600,
                        }}
                      >
                        {getInitials(partner.name)}
                      </div>
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                      {partner.name || 'Partner'}
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
                      {partner.phone || '-'}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span
                        className="font-body"
                        style={{
                          background: colors.surface,
                          color: colors.textPrimary,
                          borderRadius: '999px',
                          padding: '4px 10px',
                          fontSize: '12px',
                          fontWeight: 500,
                        }}
                      >
                        {partner.zone || '-'}
                      </span>
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
                      {Number(partner.order_count || 0)}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {Array.from({ length: Math.floor(formatScore(partner.quality_score)) }).map((_, index) => (
                          <Star key={`full-${partner.id}-${index}`} size={14} color="#D6B97B" fill="#D6B97B" />
                        ))}
                        {formatScore(partner.quality_score) % 1 >= 0.5 ? <StarHalf size={14} color="#D6B97B" fill="#D6B97B" /> : null}
                        <span className="font-body" style={{ fontSize: '12px', color: colors.textPrimary, fontWeight: 600 }}>
                          {formatScore(partner.quality_score).toFixed(1)}
                        </span>
                      </div>
                    </td>
                    <td className="font-body" style={{ padding: '16px 20px', fontSize: '14px', fontWeight: 600, color: '#D6B97B' }}>
                      {formatCurrency(partner.order_value_total)}
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <span
                        className="font-body"
                        style={{
                          background: isActive ? '#F0FDF4' : '#F3F4F6',
                          color: isActive ? '#15803D' : '#6B7280',
                          border: `1px solid ${isActive ? '#86EFAC' : '#D1D5DB'}`,
                          borderRadius: '999px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        <span style={{ width: '6px', height: '6px', borderRadius: '999px', background: isActive ? '#15803D' : '#6B7280' }} />
                        {getStatusLabel(partner)}
                      </span>
                    </td>
                    <td style={{ padding: '16px 20px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          className="font-body"
                          style={{
                            border: `1px solid ${colors.border}`,
                            borderRadius: '6px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: colors.textPrimary,
                            background: 'transparent',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            void loadPartnerDetails(partner)
                          }}
                          onMouseEnter={(event) => {
                            event.currentTarget.style.background = colors.surface
                          }}
                          onMouseLeave={(event) => {
                            event.currentTarget.style.background = 'transparent'
                          }}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="font-body"
                          onClick={() => {
                            void handleSuspendPartner(partner.id)
                          }}
                          style={{
                            border: '1px solid #FCA5A5',
                            borderRadius: '6px',
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#991B1B',
                            background: 'transparent',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(event) => {
                            event.currentTarget.style.background = '#FEF2F2'
                          }}
                          onMouseLeave={(event) => {
                            event.currentTarget.style.background = 'transparent'
                          }}
                        >
                          Suspend
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {selectedPartner ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 80,
            background: 'rgba(15, 46, 42, 0.35)',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
          onClick={() => setSelectedPartner(null)}
        >
          <div
            style={{
              width: '520px',
              height: '100%',
              background: colors.bgCard,
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
              padding: '24px',
              overflowY: 'auto',
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <div>
                <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                  Partner Details
                </p>
                <h3 className="font-display" style={{ margin: '4px 0 0', fontSize: '22px', color: colors.textPrimary }}>
                  {selectedPartner.name || 'Partner'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedPartner(null)}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}
              >
                <X size={20} color={colors.textPrimary} />
              </button>
            </div>

            {detailError ? (
              <div className="font-body" style={{ background: '#FEF2F2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: '12px', padding: '10px 12px', fontSize: '13px', marginBottom: '12px' }}>
                {detailError}
              </div>
            ) : null}

            {detailLoading ? (
              <p className="font-body" style={{ margin: 0, color: colors.textMuted }}>
                Loading partner details...
              </p>
            ) : (
              <>
                <div style={{ display: 'grid', gap: '10px', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', marginBottom: '16px' }}>
                  {[
                    { label: 'Orders Processed', value: partnerPerformance?.total_orders ?? 0 },
                    { label: 'Avg Turnaround (hrs)', value: partnerPerformance?.avg_turnaround_hours ?? '-' },
                    { label: 'Damage Rate', value: `${partnerPerformance?.damage_rate_percent ?? 0}%` },
                    { label: 'Quality Score', value: `${(partnerPerformance?.quality_score ?? formatScore(selectedPartner.quality_score)).toFixed(1)}` },
                  ].map((item) => (
                    <div key={item.label} style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '12px' }}>
                      <p className="font-body" style={{ margin: 0, fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        {item.label}
                      </p>
                      <p className="font-display" style={{ margin: '6px 0 0', fontSize: '18px', color: colors.textPrimary }}>
                        {item.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <p className="font-body" style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase' }}>
                    Adjust Quality Score
                  </p>
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="range"
                      min="0"
                      max="5"
                      step="0.5"
                      value={scoreInput}
                      onChange={(event) => setScoreInput(event.target.value)}
                      style={{ flex: 1 }}
                    />
                    <input
                      type="number"
                      min="0"
                      max="5"
                      step="0.1"
                      value={scoreInput}
                      onChange={(event) => setScoreInput(event.target.value)}
                      className="font-body"
                      style={{ width: '70px', border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '6px 8px' }}
                    />
                    <button
                      type="button"
                      className="font-body"
                      onClick={() => void handleScoreSave()}
                      disabled={savingScore}
                      style={{
                        border: 'none',
                        background: colors.textPrimary,
                        color: colors.bgCard,
                        padding: '8px 14px',
                        borderRadius: '999px',
                        cursor: savingScore ? 'not-allowed' : 'pointer',
                        opacity: savingScore ? 0.7 : 1,
                      }}
                    >
                      {savingScore ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <p className="font-body" style={{ margin: '0 0 8px', fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase' }}>
                    Monthly Trend
                  </p>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', height: '120px', padding: '12px', background: colors.bgSecondary, borderRadius: '12px' }}>
                    {trendData.map((entry) => {
                      const height = maxTrendCount > 0 ? Math.round((entry.count / maxTrendCount) * 90) : 6
                      return (
                        <div key={entry.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                          <div style={{ width: '100%', height, background: '#D6B97B', borderRadius: '8px', transition: 'height 0.2s ease' }} />
                          <span className="font-body" style={{ marginTop: '6px', fontSize: '11px', color: colors.textMuted }}>
                            {entry.label}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <p className="font-body" style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: colors.textMuted, textTransform: 'uppercase' }}>
                      Order History
                    </p>
                    <span className="font-body" style={{ fontSize: '12px', color: colors.textSecondary }}>
                      {partnerOrdersTotal} total
                    </span>
                  </div>
                  <div style={{ border: `1px solid ${colors.border}`, borderRadius: '12px', overflow: 'hidden' }}>
                    {partnerOrders.length === 0 ? (
                      <div className="font-body" style={{ padding: '12px', fontSize: '13px', color: colors.textMuted }}>
                        No orders yet for this partner.
                      </div>
                    ) : (
                      partnerOrders.map((order) => (
                        <div key={order.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', borderBottom: `1px solid ${colors.surface}` }}>
                          <div>
                            <div className="font-body" style={{ fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                              {order.order_number || order.id}
                            </div>
                            <div className="font-body" style={{ fontSize: '12px', color: colors.textMuted }}>
                              {order.customer_name || 'Customer'} · {formatDate(order.created_at)}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div className="font-body" style={{ fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>
                              {formatCurrency(order.total)}
                            </div>
                            <div className="font-body" style={{ fontSize: '11px', color: colors.textMuted }}>
                              {order.status || '-'}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {showCreateModal ? (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 70,
            background: 'rgba(0,0,0,0.40)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <form
            onSubmit={(event) => {
              void handleCreatePartner(event)
            }}
            onClick={(event) => event.stopPropagation()}
            style={{
              width: '480px',
              background: colors.bgCard,
              borderRadius: '20px',
              boxShadow: '0px 24px 64px rgba(15,46,42,0.20)',
              padding: '32px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 className="font-display" style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: colors.textPrimary }}>
                Add New Partner
              </h3>
              <button type="button" onClick={() => setShowCreateModal(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>
                <X size={20} color={colors.textPrimary} />
              </button>
            </div>

            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                  Partner Name
                </label>
                <input
                  required
                  value={formData.name}
                  onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
                  className="font-body"
                  style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px' }}
                />
              </div>

              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                  Phone
                </label>
                <input
                  required
                  value={formData.phone}
                  onChange={(event) => setFormData((prev) => ({ ...prev, phone: event.target.value }))}
                  className="font-body"
                  style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px' }}
                />
              </div>

              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                  Email <span style={{ color: colors.textMuted, fontWeight: 500 }}>(optional)</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(event) => setFormData((prev) => ({ ...prev, email: event.target.value }))}
                  className="font-body"
                  style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px' }}
                />
              </div>

              <div>
                <label className="font-body" style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                  Address
                </label>
                <textarea
                  required
                  rows={3}
                  value={formData.address}
                  onChange={(event) => setFormData((prev) => ({ ...prev, address: event.target.value }))}
                  className="font-body"
                  style={{ width: '100%', padding: '12px 16px', border: `1.5px solid ${colors.border}`, borderRadius: '10px', fontSize: '14px', resize: 'vertical' }}
                />
              </div>

              <button
                type="submit"
                className="font-body"
                style={{ width: '100%', border: 'none', borderRadius: '12px', background: '#D6B97B', color: colors.textPrimary, fontSize: '15px', fontWeight: 600, padding: '14px', marginTop: '8px', cursor: 'pointer' }}
              >
                Add Partner
              </button>

              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="font-body"
                style={{ width: '100%', border: `1.5px solid ${colors.border}`, borderRadius: '12px', background: 'transparent', color: colors.textSecondary, fontSize: '14px', fontWeight: 600, padding: '12px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  )
}
